import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, Copy, Plus, Trash2, UserPlus, Users2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState } from '@/components/ListStates';
import { useLabels } from '@/contexts/WorkspaceContext';
import { to12h } from '@/services/appointments.service';
import {
  staffService, SHIFT_STATUSES, type Shift, type ShiftStatus, type StaffMember, type CreateShiftInput,
} from '@/services/staff.service';
import { addDaysStr, formatDayShort, formatWeekRange, isToday, mondayOfStr, weekDaysOf, ymdLocal } from './weekUtils';

const STATUS_META: Record<ShiftStatus, { label: string; className: string }> = {
  scheduled: { label: 'Scheduled', className: 'bg-info-soft text-info' },
  confirmed: { label: 'Confirmed', className: 'bg-success-soft text-success' },
  completed: { label: 'Completed', className: 'bg-surface-alt text-text-muted' },
  absent: { label: 'Absent', className: 'bg-danger-soft text-danger' },
};

interface EditTarget { shift: Shift | null; date?: string; staffUid?: string }

interface Props {
  weekStart: string;
  onWeekStartChange: (next: string) => void;
  staff: StaffMember[];
  staffLoading: boolean;
  staffError: boolean;
  onRetryStaff: () => void;
  /** Takes the user to the staff directory to add someone. */
  onAddStaff: () => void;
  isAdmin: boolean;
  /** Fired after any shift mutation so the parent can refresh the week summary. */
  onChanged: () => void;
}

export default function ShiftRoster({ weekStart, onWeekStartChange, staff, staffLoading, staffError, onRetryStaff, onAddStaff, isAdmin, onChanged }: Props) {
  const t = useLabels();
  const [days, setDays] = useState<string[]>(() => weekDaysOf(weekStart));
  const [weekEnd, setWeekEnd] = useState('');
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);

  const activeStaff = staff.filter((s) => s.isActive);
  const staffByUid = new Map(staff.map((s) => [s.staffUid, s]));

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const week = await staffService.listShifts({ weekStart });
      setDays(week.days);
      setWeekEnd(week.weekEnd);
      setShifts(week.shifts);
    } catch { setLoadError(true); } finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { void load(); }, [load]);

  const refresh = () => { void load(); onChanged(); };

  const shiftsFor = (staffUid: string, date: string) => shifts.filter((s) => s.staffUid === staffUid && s.date === date);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-surface p-3 shadow-soft">
        <Button variant="ghost" size="sm" iconLeft={<ChevronLeft size={14} />} onClick={() => onWeekStartChange(addDaysStr(weekStart, -7))}>Prev</Button>
        <div className="min-w-[150px] text-center text-sm font-semibold text-text-primary">{weekEnd ? formatWeekRange(weekStart, weekEnd) : '—'}</div>
        <Button variant="ghost" size="sm" iconRight={<ChevronRight size={14} />} onClick={() => onWeekStartChange(addDaysStr(weekStart, 7))}>Next</Button>
        <Button variant="ghost" size="sm" onClick={() => onWeekStartChange(mondayOfStr(ymdLocal(new Date())))}>This week</Button>
        {isAdmin && (
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" iconLeft={<Copy size={13} />} onClick={() => setCopyOpen(true)}>Copy week</Button>
            <Button
              size="sm" iconLeft={<Plus size={13} />} disabled={!activeStaff.length}
              onClick={() => setEditing({ shift: null, date: weekStart, staffUid: activeStaff[0]?.staffUid })}
            >
              Add shift
            </Button>
          </div>
        )}
      </div>

      {staffLoading ? (
        <div className="skeleton h-64 rounded-2xl" />
      ) : staffError ? (
        <LoadErrorState what="your staff" onRetry={onRetryStaff} />
      ) : !activeStaff.length ? (
        <EmptyState
          icon={<Users2 size={28} />}
          title="No active staff to schedule"
          description={isAdmin ? 'Add a staff member first, then build the weekly rota.' : 'Once an admin adds your team, the weekly rota is built here.'}
          action={isAdmin ? <Button iconLeft={<UserPlus size={14} />} onClick={onAddStaff}>Add {t('staff', 'singular').toLowerCase()}</Button> : undefined}
        />
      ) : loading ? (
        <div className="skeleton h-64 rounded-2xl" />
      ) : loadError ? (
        <LoadErrorState what="this week's shifts" onRetry={() => void load()} />
      ) : (
        <>
          {/* Desktop week grid — staff rows × day columns. Only shown at xl+ where there's room for 8 columns. */}
          <Card padded={false} className="hidden overflow-x-auto xl:block">
            <div className="grid min-w-[920px] grid-cols-[160px_repeat(7,1fr)]">
              <div className="border-b border-r border-border bg-surface-alt/40 p-2 text-2xs font-semibold uppercase tracking-wide text-text-muted">Staff</div>
              {days.map((d) => {
                const { weekday, day } = formatDayShort(d);
                return (
                  <div key={d} className={clsx('border-b border-border p-2 text-center text-2xs font-semibold uppercase tracking-wide', isToday(d) ? 'bg-primary-soft text-primary' : 'bg-surface-alt/40 text-text-muted')}>
                    {weekday} <span className="font-normal normal-case">{day}</span>
                  </div>
                );
              })}
              {activeStaff.map((s) => (
                <StaffRowCells
                  key={s.staffUid}
                  s={s}
                  days={days}
                  shiftsFor={shiftsFor}
                  isAdmin={isAdmin}
                  onAdd={(date) => setEditing({ shift: null, date, staffUid: s.staffUid })}
                  onEdit={(shift) => setEditing({ shift })}
                />
              ))}
            </div>
          </Card>

          {/* Mobile/tablet — stacked by day, below the xl breakpoint where 8 grid columns won't fit the pane. */}
          <div className="space-y-3 xl:hidden">
            {days.map((d) => {
              const dayShifts = shifts.filter((sh) => sh.date === d).sort((a, b) => a.start.localeCompare(b.start));
              const { weekday, day } = formatDayShort(d);
              return (
                <Card key={d} className={clsx(isToday(d) && 'border-primary/40')}>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-semibold text-text-primary">{weekday}, {day}</div>
                    {isAdmin && (
                      <button
                        onClick={() => setEditing({ shift: null, date: d, staffUid: activeStaff[0]?.staffUid })}
                        className="text-2xs font-semibold text-primary"
                      >
                        + Add shift
                      </button>
                    )}
                  </div>
                  {dayShifts.length === 0 ? (
                    <div className="text-2xs text-text-muted">No shifts scheduled</div>
                  ) : (
                    <div className="space-y-1.5">
                      {dayShifts.map((sh) => {
                        const person = staffByUid.get(sh.staffUid);
                        return (
                          <button
                            key={sh.shiftUid}
                            onClick={() => isAdmin && setEditing({ shift: sh })}
                            className="flex w-full items-center gap-2 rounded-lg border border-border px-2.5 py-1.5 text-left hover:bg-surface-alt"
                          >
                            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: person?.color ?? '#999' }} />
                            <span className="min-w-0 flex-1 truncate text-xs font-medium text-text-primary">{person?.name ?? 'Unknown'}</span>
                            <span className="shrink-0 text-2xs text-text-muted">{to12h(sh.start)}–{to12h(sh.end)}{sh.overnight ? ' (+1d)' : ''}</span>
                            <span className={clsx('shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold', STATUS_META[sh.status].className)}>{STATUS_META[sh.status].label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}

      <ShiftFormModal
        open={!!editing}
        shift={editing?.shift ?? null}
        defaultDate={editing?.date}
        defaultStaffUid={editing?.staffUid}
        staff={activeStaff}
        isAdmin={isAdmin}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); refresh(); }}
        onDeleted={() => { setEditing(null); refresh(); }}
      />
      <CopyWeekModal open={copyOpen} onClose={() => setCopyOpen(false)} fromWeekStart={weekStart} onCopied={() => { setCopyOpen(false); refresh(); }} />
    </div>
  );
}

function StaffRowCells({
  s, days, shiftsFor, isAdmin, onAdd, onEdit,
}: {
  s: StaffMember;
  days: string[];
  shiftsFor: (staffUid: string, date: string) => Shift[];
  isAdmin: boolean;
  onAdd: (date: string) => void;
  onEdit: (shift: Shift) => void;
}) {
  return (
    <>
      <div className="flex items-center gap-2 border-b border-r border-border p-2">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
        <div className="min-w-0">
          <div className="truncate text-xs font-medium text-text-primary">{s.name}</div>
          <div className="truncate text-[10px] text-text-muted">{s.role}</div>
        </div>
      </div>
      {days.map((d) => {
        const cellShifts = shiftsFor(s.staffUid, d);
        return (
          <div key={d} className={clsx('group relative min-h-[64px] border-b border-border p-1.5', isToday(d) && 'bg-primary-soft/30')}>
            <div className="space-y-1">
              {cellShifts.map((sh) => (
                <button
                  key={sh.shiftUid}
                  onClick={() => isAdmin && onEdit(sh)}
                  className={clsx('block w-full rounded-md px-1.5 py-1 text-left text-[10px] leading-tight', STATUS_META[sh.status].className)}
                >
                  <div className="font-semibold">{to12h(sh.start)}–{to12h(sh.end)}{sh.overnight ? ' +1d' : ''}</div>
                  {sh.station && <div className="truncate opacity-80">{sh.station}</div>}
                </button>
              ))}
            </div>
            {isAdmin && (
              <button
                onClick={() => onAdd(d)}
                className="absolute right-1 top-1 hidden h-5 w-5 items-center justify-center rounded-md bg-surface text-text-muted opacity-0 shadow-soft transition-opacity group-hover:flex group-hover:opacity-100 hover:text-primary"
                aria-label="Add shift"
              >
                <Plus size={11} />
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}

function ShiftFormModal({
  open, shift, defaultDate, defaultStaffUid, staff, isAdmin, onClose, onSaved, onDeleted,
}: {
  open: boolean; shift: Shift | null; defaultDate?: string; defaultStaffUid?: string;
  staff: StaffMember[]; isAdmin: boolean;
  onClose: () => void; onSaved: () => void; onDeleted: () => void;
}) {
  const [staffUid, setStaffUid] = useState('');
  const [date, setDate] = useState('');
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('17:00');
  const [breakMinutes, setBreakMinutes] = useState('0');
  const [station, setStation] = useState('');
  const [status, setStatus] = useState<ShiftStatus>('scheduled');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (shift) {
      setStaffUid(shift.staffUid); setDate(shift.date); setStart(shift.start); setEnd(shift.end);
      setBreakMinutes(String(shift.breakMinutes)); setStation(shift.station ?? ''); setStatus(shift.status); setNotes(shift.notes ?? '');
    } else {
      setStaffUid(defaultStaffUid ?? staff[0]?.staffUid ?? '');
      setDate(defaultDate ?? ymdLocal(new Date()));
      setStart('09:00'); setEnd('17:00'); setBreakMinutes('0'); setStation(''); setStatus('scheduled'); setNotes('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shift, defaultDate, defaultStaffUid]);

  const save = async () => {
    if (!staffUid) { toast.error('Choose a staff member'); return; }
    if (!date || !start || !end) { toast.error('Date, start and end are required'); return; }
    setBusy(true);
    const payload: CreateShiftInput = {
      staffUid, date, start, end,
      breakMinutes: Math.max(0, parseInt(breakMinutes, 10) || 0),
      station: station.trim() || undefined,
      status,
      notes: notes.trim() || undefined,
    };
    try {
      if (shift) await staffService.updateShift(shift.shiftUid, payload);
      else await staffService.createShift(payload);
      toast.success(shift ? 'Shift updated' : 'Shift added');
      onSaved();
    } catch (e: any) { if (!e?.response) toast.error('Could not save this shift.'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!shift) return;
    if (!window.confirm('Delete this shift?')) return;
    setBusy(true);
    try { await staffService.removeShift(shift.shiftUid); toast.success('Shift deleted'); onDeleted(); }
    catch (e: any) { if (!e?.response) toast.error('Could not delete this shift.'); }
    finally { setBusy(false); }
  };

  const readOnly = !isAdmin;

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title={shift ? 'Edit shift' : 'Add shift'} widthClass="max-w-md">
      <FormField label="Staff member" required>
        <Select value={staffUid} onChange={(e) => setStaffUid(e.target.value)} disabled={readOnly}>
          {staff.map((s) => <option key={s.staffUid} value={s.staffUid}>{s.name} — {s.role}</option>)}
        </Select>
      </FormField>
      <FormField label="Date" required><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={readOnly} /></FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Start" required><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} disabled={readOnly} /></FormField>
        <FormField label="End" required hint="Earlier than start = overnight"><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} disabled={readOnly} /></FormField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Break (min)"><Input type="number" min={0} max={600} value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} disabled={readOnly} /></FormField>
        <FormField label="Station" hint="Optional"><Input value={station} onChange={(e) => setStation(e.target.value)} placeholder="Grill, Front…" disabled={readOnly} /></FormField>
      </div>
      {shift && (
        <FormField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value as ShiftStatus)} disabled={readOnly}>
            {SHIFT_STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </Select>
        </FormField>
      )}
      <FormField label="Notes"><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={readOnly} /></FormField>

      <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-4">
        {shift && isAdmin ? (
          <Button variant="danger" size="sm" iconLeft={<Trash2 size={13} />} onClick={remove} disabled={busy}>Delete</Button>
        ) : <span />}
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          {isAdmin && <Button variant="primary" onClick={save} loading={busy} disabled={busy}>{shift ? 'Save' : 'Add'}</Button>}
        </div>
      </div>
    </Modal>
  );
}

function CopyWeekModal({
  open, onClose, fromWeekStart, onCopied,
}: { open: boolean; onClose: () => void; fromWeekStart: string; onCopied: () => void }) {
  const [toWeekStart, setToWeekStart] = useState(() => addDaysStr(fromWeekStart, 7));
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setToWeekStart(mondayOfStr(addDaysStr(fromWeekStart, 7))); }, [open, fromWeekStart]);

  const run = async () => {
    setBusy(true);
    try {
      const monday = mondayOfStr(toWeekStart);
      const r = await staffService.copyWeek(fromWeekStart, monday);
      toast.success(`Copied ${r.copied} shift${r.copied === 1 ? '' : 's'}${r.skipped ? `, skipped ${r.skipped} overlapping` : ''}`);
      onCopied();
    } catch (e: any) { if (!e?.response) toast.error('Could not copy this week.'); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title="Copy week" widthClass="max-w-sm">
      <p className="mb-3 text-xs text-text-muted">Copies every shift from the current week onto the target week (same weekday, status reset to scheduled). Shifts that would overlap an existing one on the target week are skipped.</p>
      <FormField label="Copy to week starting"><Input type="date" value={toWeekStart} onChange={(e) => setToWeekStart(e.target.value)} /></FormField>
      <div className="mt-4 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={run} loading={busy} disabled={busy}>Copy</Button>
      </div>
    </Modal>
  );
}
